import { ElementStore } from 'react-form-builder2';
import ID from 'react-form-builder2/lib/UUID';
import { DEFAULT_ADDRESS_ELEMENTS } from '../CustomBlocks/AddressInput';
import { DEFAULT_AI_CONFIG } from '../CustomBlocks/aiGenerator/aiConfig';
import { createDefaultSlotConfig } from '../CustomBlocks/slotBooking/slotTypes';
import { QUIZ_BLOCK_DEFAULTS } from '../CustomBlocks/quiz/quizTypes';
import { SCALE_BLOCK_DEFAULTS } from '../CustomBlocks/scale/scaleTypes';


export const ACTION_BLOCK_KEYS = new Set([
  'CustomButton',
  'PaymentSection',
  'KycButton',
  'ESignButton',
]);


export const DEFAULT_FIELD_NAMES: Record<string, string> = {
  NameInput: 'name',
  EmailInput: 'email',
  NumberInput: 'number',
  PhoneInput: 'phone',
  AddressInput: 'address',
  DateInput: 'date',
  TimeInput: 'time',
  DateTimeInput: 'date_time',
  MonthYearInput: 'month_year',
  CustomButton: 'submit',
  TextInput: 'text_input',
  TextArea: 'text_area',
  Tags: 'multi_select',
  ChipSelect: 'chip_select',
  RadioButtons: 'radio',
  Checkboxes: 'checkbox',
  PaymentSection: 'payment',
  KycButton: 'kyc',
  ESignButton: 'esign',
  AiGenerator: 'ai_content',
  CrmAutocomplete: 'crm_select',
  SlotBooking: 'booking_slot',
  RatingInput: 'rating',
  OpinionScale: 'opinion_scale',
  SliderInput: 'slider',
};

export const REQUIRED_BY_DEFAULT_KEYS = new Set(['EmailInput', 'PhoneInput']);

const DATA_IS_REQUIRED_BY_DEFAULT_KEYS = new Set([
  'PaymentSection',
  'KycButton',
  'ESignButton',
]);

const applyDefaultFieldName = (field: any, data: any[]) => {
  if (!field || field.field_name) return;

  const base = DEFAULT_FIELD_NAMES[field.key];
  if (!base) return;

  const taken = new Set(
    data
      .filter((f) => f !== field)
      .map((f) => f?.field_name)
      .filter(Boolean),
  );

  let candidate = base;
  let n = 2;
  while (taken.has(candidate)) {
    candidate = `${base}_${n}`;
    n += 1;
  }
  field.field_name = candidate;
};


const applySeedData = (field: any) => {
  if (!field) return;

  if (REQUIRED_BY_DEFAULT_KEYS.has(field.key)) {
    field.required = true;
  }

  if (DATA_IS_REQUIRED_BY_DEFAULT_KEYS.has(field.key)) {
    if (!field.data) field.data = {};
    if (field.data.isRequired === undefined) field.data.isRequired = true;
  }

  // A dropped button defaults to a submit button.
  if (field.key === 'CustomButton') {
    if (!field.data) field.data = {};
    if (!field.data.type) field.data.type = 'submit';
  }


  if (field.key === 'SlotBooking' && !field.slotConfig) {
    field.slotConfig = createDefaultSlotConfig();
  }

  if (QUIZ_BLOCK_DEFAULTS[field.key] && !field.blockConfig) {
    field.blockConfig = QUIZ_BLOCK_DEFAULTS[field.key]();
  }

  if (SCALE_BLOCK_DEFAULTS[field.key] && !field.blockConfig) {
    field.blockConfig = SCALE_BLOCK_DEFAULTS[field.key]();
  }

  if (field.key === 'AddressInput' && !field.addressElements) {
    field.addressElements = DEFAULT_ADDRESS_ELEMENTS.map((el) => ({ ...el }));
    field.showElementsLabel = true;
  }

  // A dropped AI Generator starts with one usable block, referencing its own
  // main input, so it does something out of the box instead of sitting empty.
  if (field.key === 'AiGenerator') {
    if (!field.blocks) {
      field.outputFormat = field.outputFormat || 'markdown';
      field.mainInputLabel = field.mainInputLabel ?? 'Describe what this is about';
      field.blocks = [
        {
          id: ID.uuid(),
          label: 'Key Point',
          description: '',
          prompt: `Write a short, useful point about: {{${field.field_name}}}`,
          regenerable: true,
          editable: true,
        },
      ];
    }
    if (!field.aiConfig) field.aiConfig = { ...DEFAULT_AI_CONFIG };
    if (!Array.isArray(field.prompts)) field.prompts = [];
    // Per-setting toggles default to off (resolveRespondentEditable falls back
    // to the legacy single respondentConfigurable flag for older fields, so it
    // is deliberately not seeded here).
    if (!field.respondentEditable) field.respondentEditable = {};
  }
};


const moveAboveTrailingActions = (
  field: any,
  data: any[],
): { addedIndex: number; moved: boolean } => {
  const lastIndex = data.length - 1;
  if (!field || ACTION_BLOCK_KEYS.has(field.key)) {
    return { addedIndex: lastIndex, moved: false };
  }

  let insertAt = lastIndex;
  while (insertAt > 0 && ACTION_BLOCK_KEYS.has(data[insertAt - 1]?.key)) {
    insertAt -= 1;
  }
  if (insertAt === lastIndex) return { addedIndex: lastIndex, moved: false };

  data.splice(lastIndex, 1);
  data.splice(insertAt, 0, field);

  setTimeout(() => {
    (ElementStore as any).dispatch('updateOrder', data);
  }, 0);

  return { addedIndex: insertAt, moved: true };
};


const openFieldProperties = (addedIndex: number, moved: boolean) => {
  setTimeout(
    () => {
      const items = document.querySelectorAll<HTMLElement>(
        '.react-form-builder-preview .Sortable > .rfb-item.SortableItem',
      );
      const addedItem = items[addedIndex] ?? items[items.length - 1];
      addedItem
        ?.querySelector<HTMLElement>('.toolbar-header-buttons .btn')
        ?.click();
    },
    moved ? 16 : 0,
  );
};

export const initializeAddedField = (data: any[]) => {
  if (!Array.isArray(data) || !data.length) return;

  const field = data[data.length - 1];
  applyDefaultFieldName(field, data);
  applySeedData(field);

  const { addedIndex, moved } = moveAboveTrailingActions(field, data);
  openFieldProperties(addedIndex, moved);
};
